import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CreateBuyerListInput,
  CreateBuyerListMutation,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../useGraphQlClient";

export type BuyerContextSource =
  | "In-row"
  | "Bulk action"
  | "buyer-profile"
  | "buyer lists page"
  | "supplier-profile"
  | "Notice preview"
  | "Save drawer";

const createBuyerListMutation = graphql(`
  mutation createBuyerList($input: CreateBuyerListInput!) {
    createBuyerList(input: $input) {
      id
      name
      companyId
      ownerId
      sharingType
      createdAt
      updatedAt
      isDeleted
    }
  }
`);

export function useCreateBuyerList(
  contextSource: BuyerContextSource = "In-row",
  options?: UseMutationOptions<CreateBuyerListMutation, unknown, CreateBuyerListInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();
  const { mutate: innerMutate, ...rest } = useGraphQlMutation(createBuyerListMutation, {
    onSuccess: (data, variables, context) => {
      const list = data.createBuyerList;
      void queryClient.invalidateQueries(["buyerLists"]);
      void queryClient.invalidateQueries(["supplierStatsOrganisation"]);
      void queryClient.invalidateQueries(["buyers", "buyersForSupplier"]);
      logEvent(EventNames.buyerListCreated, {
        "List id": list.id,
        "List name": list.name,
        "Context source": contextSource,
      });
      options?.onSuccess?.(data, variables.input, context);
    },
  });
  return { ...rest, mutate: (input: CreateBuyerListInput) => innerMutate({ input }) };
}
